import { useEffect, useRef, type ReactNode } from 'react';
import type { Translate } from '@elec/i18n';
import { Icon } from './ui.tsx';

/** Sections of the right sidebar, in order. */
export type SideSection = 'selection' | 'assistant' | 'sheet';
export const SIDE_SECTIONS: readonly SideSection[] = ['selection', 'assistant', 'sheet'];

export interface SidebarState {
  /** Folded to a rail of icons. */
  folded: boolean;
  /** Sections open while the sidebar is expanded. */
  open: Record<SideSection, boolean>;
  /** Section shown beside the rail while folded (its icon was clicked). */
  flyout: SideSection | null;
}

export const DEFAULT_SIDEBAR: SidebarState = {
  folded: false,
  open: { selection: true, assistant: true, sheet: true },
  flyout: null,
};

/** Read the saved sidebar state (folded, open sections); anything unreadable gives the default. */
export function parseSidebar(saved: string | null): SidebarState {
  try {
    const v = JSON.parse(saved ?? '') as Partial<SidebarState>;
    return {
      folded: !!v.folded,
      open: { ...DEFAULT_SIDEBAR.open, ...(v.open ?? {}) },
      flyout: null,
    };
  } catch {
    return DEFAULT_SIDEBAR;
  }
}

/** The state to open one section: unfolded, it opens in place; folded, beside the rail. */
export function showSection(s: SidebarState, id: SideSection): SidebarState {
  return s.folded ? { ...s, flyout: id } : { ...s, open: { ...s.open, [id]: true } };
}

export interface SidebarItem {
  id: SideSection;
  icon: string;
  title: string;
  /** Small count on the icon and the header (e.g. selected items). */
  badge?: number;
  content: ReactNode;
}

/**
 * Right sidebar: sections that fold open and shut. A button folds the whole sidebar to a rail of
 * icons, one per section; clicking an icon shows that section beside the rail. Every section stays
 * mounted, hidden when shut, so what it holds (the assistant's conversation) is kept.
 */
export function Sidebar(props: {
  t: Translate;
  state: SidebarState;
  onChange(s: SidebarState): void;
  items: SidebarItem[];
}) {
  const { t, state, onChange } = props;
  const root = useRef<HTMLElement>(null);
  const folded = state.folded;

  // Folded: a click elsewhere puts the section away (not in menus, dialogs or the tutorial).
  useEffect(() => {
    if (!folded || !state.flyout) return;
    const close = (e: MouseEvent) => {
      const n = e.target as Element | null;
      if (!n || root.current?.contains(n) || n.closest?.('.menu-list, .backdrop, .tour')) return;
      onChange({ ...state, flyout: null });
    };
    window.addEventListener('mousedown', close);
    return () => window.removeEventListener('mousedown', close);
  }, [folded, state, onChange]);

  const badge = (n: number | undefined) =>
    n ? (
      <span className="side-badge" aria-hidden="true">
        {n}
      </span>
    ) : null;

  return (
    <aside
      className={`inspector sidebar${folded ? ' folded' : ''}`}
      aria-label={t('side.label')}
      ref={root}
      data-testid="sidebar"
    >
      <div className="side-top">
        <button
          className="icon-btn side-fold"
          onClick={() => onChange({ ...state, folded: !folded, flyout: null })}
          title={folded ? t('side.unfold') : t('side.fold')}
          aria-label={folded ? t('side.unfold') : t('side.fold')}
          aria-expanded={!folded}
          data-testid="side-fold"
        >
          <Icon name={folded ? 'side-unfold' : 'side-fold'} />
        </button>
      </div>
      {folded && (
        <nav className="side-rail" aria-label={t('side.label')}>
          {props.items.map((it) => (
            <button
              key={it.id}
              className={`icon-btn side-icon${state.flyout === it.id ? ' active' : ''}`}
              onClick={() => onChange({ ...state, flyout: state.flyout === it.id ? null : it.id })}
              title={it.title}
              aria-label={it.title}
              aria-pressed={state.flyout === it.id}
              data-testid={`side-icon-${it.id}`}
            >
              <Icon name={it.icon} />
              {badge(it.badge)}
            </button>
          ))}
        </nav>
      )}
      <div className={folded ? `side-flyout${state.flyout ? ' open' : ''}` : 'side-sections'}>
        {props.items.map((it) => {
          const shown = folded ? state.flyout === it.id : true;
          const open = folded || state.open[it.id];
          return (
            <section
              key={it.id}
              className={`side-sec${open ? ' open' : ''}`}
              data-section={it.id}
              hidden={!shown}
            >
              <button
                className="side-sec-head"
                onClick={() =>
                  folded
                    ? onChange({ ...state, flyout: null })
                    : onChange({ ...state, open: { ...state.open, [it.id]: !open } })
                }
                aria-expanded={open}
                data-testid={`side-sec-${it.id}`}
              >
                <Icon name={it.icon} size={16} />
                <span className="side-sec-title">{it.title}</span>
                {badge(it.badge)}
                <span className="side-chevron">
                  <Icon name={folded ? 'close' : 'chevron'} size={16} />
                </span>
              </button>
              <div className="side-sec-body" hidden={!open}>
                {it.content}
              </div>
            </section>
          );
        })}
      </div>
    </aside>
  );
}
